import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import axios from 'axios';
import { Logger } from './loglySetup';
import { sanitizeExcelCell } from './sanitizeExcelCell';
import CloseableErrorMessage from '../components/CloseableErrorMessage';
import { ColumnType } from 'antd/lib/table';

export interface TableAndExcelColumn<T> extends ColumnType<T> {
  visible: boolean;
  excelHeader: string;
  excelRender(data: T): string | Date | number;
}

export interface ExcelWorkbook {
  fileName: string;
  worksheets: ExcelWorksheet[];
}
export interface ExcelWorksheet {
  name: string;
  columns: TableAndExcelColumn<any>[];
  data: any[];
}

export interface ExportToExcelProps {
  excelWorkbook: ExcelWorkbook
}

export function exportToExcel({
  excelWorkbook
}: ExportToExcelProps) {
  /**
   * Set column widths based on string length
   * @param taskData
   * @returns array of column widths
   */
  const fitToColumn = (columns: TableAndExcelColumn<any>[], taskData: any[]) => {
    const cols: { wch: number }[] = [];
    const columnData = getColumnData(columns, taskData);

    columnData.forEach(data => {
      for (const [key] of Object.entries(data)) {
        cols.push({
          wch: Math.max(
            ...columnData.map((a) =>
              a[key]?.length > 10
                ? a[key].length
                : key.length > 10
                ? key.length
                : 10
            )
          ),
        });
      }
    })

    return cols;
  };

  const getColumnData = (columns: TableAndExcelColumn<any>[], taskData: any[]) => {
    const columnData = taskData.map((d) => {
      const rowData : any = {};
      columns.filter(c => c.visible).forEach(c => {
        rowData[sanitizeExcelCell(c.excelHeader)] = sanitizeExcelCell(c.excelRender(d));
      })

      return rowData;
    })

    return columnData;
  }

  try {
    let sheets: any = {};
    let sheetNames: string[] = [];
    excelWorkbook.worksheets.forEach(worksheet => {
      const ws = XLSX.utils.json_to_sheet(getColumnData(worksheet.columns, worksheet.data))
      ws['!cols'] = fitToColumn(worksheet.columns, worksheet.data);
      sheets[worksheet.name] = ws;
      sheetNames.push(worksheet.name);
    });
    const wb = {
      Sheets: sheets,
      SheetNames: sheetNames,
    };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const dataToExport = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    FileSaver.saveAs(
      dataToExport,
      `${excelWorkbook.fileName.replace(/ /g, '_')}_${new Date()
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '_')}.xlsx`
    );
  } catch (e) {
    if (axios.isCancel(e)) Logger().log(e);
    else {
      Logger().error(e);
      CloseableErrorMessage('Error occurred while exporting XLS');
    }
  }
}
